import { useEffect } from 'react';
import {
  applyConstructorOverlay,
  clearConstructor,
  getConstructorBaseline,
  setConstructorBaseline,
} from './apply';
import type { ConstructorValue } from './types';

/**
 * `?design_constructor=child` flag — set by the Design Constructor parent
 * when it embeds the iframe app inside its preview pane. Drives all
 * child-mode behaviour (postMessage listener, click-to-edit selection,
 * hover outline) so production builds without the flag stay zero-overhead.
 */
export function isChildMode(): boolean {
  if (typeof window === 'undefined') return false;
  const v = new URLSearchParams(window.location.search).get('design_constructor');
  return v === 'child';
}

interface ApplyMsg {
  type: 'design-constructor-apply';
  value: ConstructorValue;
}
interface ClearMsg {
  type: 'design-constructor-clear';
}
type Msg = ApplyMsg | ClearMsg;

function isMsg(x: unknown): x is Msg {
  if (typeof x !== 'object' || x === null) return false;
  const t = (x as { type?: unknown }).type;
  return t === 'design-constructor-apply' || t === 'design-constructor-clear';
}

/**
 * When current document is loaded as iframe child of the Design Constructor
 * (`?design_constructor=child`), listen for postMessage events from parent
 * and apply / clear the received ConstructorValue on this document's :root.
 * No-op when flag is absent (zero overhead in production).
 *
 * Side-effect: in child-mode the hook also stamps
 * `data-design-constructor="child"` on `<html>` so the global hover-outline
 * CSS in `index.css` activates. The attribute is set imperatively once on
 * mount (and removed on unmount), NOT in the render output, so the
 * MutationObserver in `BottomAction.tsx` (which watches `:root` inline
 * style for `--co-BottomActionStyle-style`) doesn't re-fire on every render.
 */
export function useChildListener(): void {
  useEffect(() => {
    if (!isChildMode()) return;
    document.documentElement.setAttribute('data-design-constructor', 'child');

    // On mount, push the current backend baseline up to the parent. The
    // baseline may not be loaded yet (ThemeProvider fetch lands later),
    // but `setConstructorBaseline` will broadcast again from inside
    // apply.ts once it does — so the parent ultimately receives the full
    // partner theme either way. This call covers the case where
    // ThemeProvider already finished before we mounted.
    const baseline = getConstructorBaseline();
    window.parent?.postMessage(
      { type: 'design-constructor-baseline', value: baseline },
      '*',
    );

    const handler = (e: MessageEvent) => {
      const data: unknown = e.data;
      if (!isMsg(data)) return;
      if (data.type === 'design-constructor-apply') {
        applyConstructorOverlay(data.value);
      } else {
        clearConstructor();
      }
    };
    window.addEventListener('message', handler);
    return () => {
      window.removeEventListener('message', handler);
      document.documentElement.removeAttribute('data-design-constructor');
    };
  }, []);
}

/**
 * Parent-side baseline sync — listens for `design-constructor-baseline`
 * messages from the embedded child iframe and stores the received
 * ConstructorValue as the parent's backend baseline. Copy JSON in the
 * parent uses this baseline to assemble the full theme (existing partner
 * theme ⊕ current-session tweaks).
 *
 * Outside parent-mode this hook is harmless — the message simply never
 * arrives. No-op until the iframe child posts the message.
 */
export function useBaselineSync(): void {
  useEffect(() => {
    const handler = (e: MessageEvent) => {
      const data = e.data as { type?: unknown; value?: unknown } | null;
      if (!data || data.type !== 'design-constructor-baseline') return;
      if (typeof data.value !== 'object' || data.value === null) return;
      setConstructorBaseline(data.value as ConstructorValue);
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);
}

/**
 * Click-to-edit listener — when the iframe is in child-mode the operator
 * can still use the site normally (single click → RIP opens the pack,
 * slider arrows → next/prev, category pill → switch category). To pick an
 * element for editing the operator **right-clicks** it; the closest
 * ancestor with `data-co-component` is sent to the parent constructor.
 *
 * Why contextmenu and not click/dblclick:
 *   - Single click must keep production semantics so the operator can drive
 *     the real flow inside the preview iframe.
 *   - dblclick fires AFTER a single click already executed, so by the time
 *     we'd detect it the production action has already happened (e.g. RIP
 *     already opened the purchase modal). Right-click never triggers
 *     production click handlers, so swapping in editing here is clean.
 *   - This mirrors the dev-tools "Inspect Element" pattern operators
 *     already know.
 *
 * Outside child-mode the hook is an inert no-op — the early return runs
 * before any DOM listener registers, so production builds pay zero cost.
 *
 * `closest()` walks ancestors and stops at the FIRST match — a right-click
 * on the inner `RipButton` returns `RipButton`, not the wrapping
 * `BottomActionShell`, letting the operator drill into the most specific
 * child first.
 */
export function useClickToEdit(): void {
  useEffect(() => {
    if (!isChildMode()) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      let co = target?.closest('[data-co-component]') ?? null;
      // Fallback: when the cursor sits on a "blank" content-overlay
      // area (e.g. the empty reveal viewport between slot-tiles), the
      // ancestor chain doesn't reach the page background — it's a
      // sibling layer behind `z-10` content, not an ancestor. Walk the
      // hit-stack via `elementsFromPoint`; pick the first element
      // (any depth) that owns or descends from a `data-co-component`.
      if (!co) {
        const stack = document.elementsFromPoint(e.clientX, e.clientY);
        for (const el of stack) {
          const hit = (el as Element).closest('[data-co-component]');
          if (hit) {
            co = hit;
            break;
          }
        }
      }
      if (!co) return; // truly editable-less area — let the native menu show
      const name = co.getAttribute('data-co-component');
      if (!name) return;
      // Optional: which variant of the component the user clicked. Set by
      // DS components that render visually-distinct variants from one JSX
      // prop (e.g. Button → 'primary' | 'glass' | 'accent-gloss'). When
      // present the parent's variant-picker auto-switches to it so the
      // operator immediately sees the knobs for the kind of button they
      // clicked, not the picker's default `variants[0]`.
      const variant = co.getAttribute('data-co-variant') ?? undefined;
      e.preventDefault();
      e.stopPropagation();
      window.parent?.postMessage(
        { type: 'design-constructor-select', name, variant },
        '*',
      );
    };
    document.addEventListener('contextmenu', handler, true);
    return () => document.removeEventListener('contextmenu', handler, true);
  }, []);
}
